/**
 * - このコンポーネントは他のnext/headコンポーネントの中でネストして使用しない。
 *   ネストするとページレンダリング時に本コンポーネントが消失するため。
 * - 親コンポーネントでnext/headを使用し、本コンポーネントからはscriptタグのみreturnした場合、scriptタグが正しくレンダリングされない。
 *   そのため親コンポーネントではnext/headを使用せず、本コンポーネントからはnext/headとscriptタグをセットでreturnする。
 */
import NextHead from 'next/head';

type StructuredData = {
  '@context'?: string;
  '@type': string | string[];
  [key: string]: any;
};

type JsonLdProps = {
  data: StructuredData | StructuredData[];
};

export const JsonLd = ({ data }: JsonLdProps) => {
  const jsonLd = Array.isArray(data)
    ? data.map(createJsonLd)
    : createJsonLd(data);

  return (
    <NextHead>
      <script
        type="application/ld+json"
        // JSONLDの値をXSS対策でエスケープする
        // HTMLエスケープ（< → &lt;等）だとJSONLDのパーサ側が元の文字列に復元できないため
        // HTMLエスケープは使用しない
        // また「<」単体などを不要に置換しないよう限定的に置換する
        // ref: https://github.com/garmeeh/next-seo/blob/main/src/utils/stringify.ts
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(jsonLd)
            .replace(/<\/script/gi, '\\u003C/script')
            .replace(/<!--/g, '\\u003C!--')
            .replace(/-->/g, '--\\u003E'),
        }}
      />
    </NextHead>
  );
};

const createJsonLd = (item: StructuredData) => {
  const {
    '@context': context = 'https://schema.org',
    '@type': type,
    ...props
  } = item;
  return {
    '@context': context,
    '@type': type,
    ...props,
  };
};
